import { FC, useMemo, useState } from "react"
import { makeStyles } from "@material-ui/core"
import clsx from "clsx"

import { colors } from "../../utils/theme"

/** Represents a single line within a key legend. */
type LegendItem = {
  label: string
  value: string
  color: string
}

/** Available legend collections. */
export type DBTKeyType = "meals" | "skills" | "emotions" | "urges" | "weekly"

const KEY_COLLECTIONS: Record<
  DBTKeyType,
  { title: string; entries: LegendItem[] }
> = {
  meals: {
    title: "Key: Meals",
    entries: [
      { value: "0", label: "Caution", color: colors.dbt.intensity[7] },
      { value: "1", label: "Possible concern", color: colors.dbt.intensity[6] },
      { value: "2", label: "Somewhat low", color: colors.dbt.intensity[2] },
      { value: "3", label: "Normal", color: colors.dbt.intensity[1] },
      { value: "4", label: "Somewhat high", color: colors.dbt.intensity[2] },
      { value: "5", label: "Possible concern", color: colors.dbt.intensity[6] },
      { value: "6+", label: "Caution", color: colors.dbt.intensity[7] },
    ],
  },
  skills: {
    title: "Key: Used Skills",
    entries: [
      {
        value: "0",
        label: "Not thought about or used",
        color: colors.dbt.intensity[0],
      },
      {
        value: "1",
        label: "Thought about, not used, didn't want to",
        color: colors.dbt.intensity[1],
      },
      {
        value: "2",
        label: "Thought about, not used, wanted to",
        color: colors.dbt.intensity[2],
      },
      {
        value: "3",
        label: "Tried but couldn't use them",
        color: colors.dbt.intensity[3],
      },
      {
        value: "4",
        label: "Tried, could do them but they didn't help",
        color: colors.dbt.intensity[4],
      },
      {
        value: "5",
        label: "Tried, could use them, helped",
        color: colors.dbt.intensity[5],
      },
      {
        value: "6",
        label: "Didn't try, used them, didn't help",
        color: colors.dbt.intensity[6],
      },
      {
        value: "7",
        label: "Didn't try, used them, helped",
        color: colors.dbt.intensity[7],
      },
    ],
  },
  emotions: {
    title: "Key: Emotions",
    entries: [
      { value: "0", label: "Not at all", color: colors.dbt.intensity[0] },
      { value: "1", label: "A bit", color: colors.dbt.intensity[1] },
      { value: "2", label: "Somewhat", color: colors.dbt.intensity[2] },
      { value: "3", label: "Rather strong", color: colors.dbt.intensity[4] },
      { value: "4", label: "Very strong", color: colors.dbt.intensity[6] },
      { value: "5", label: "Extremely strong", color: colors.dbt.intensity[7] },
    ],
  },
  urges: {
    title: "Key: Urges & Behaviors",
    entries: [
      { value: "0", label: "Not at all", color: colors.dbt.intensity[0] },
      { value: "1", label: "A bit", color: colors.dbt.intensity[1] },
      { value: "2", label: "Somewhat", color: colors.dbt.intensity[2] },
      { value: "3", label: "Rather strong", color: colors.dbt.intensity[4] },
      { value: "4", label: "Very strong", color: colors.dbt.intensity[6] },
      { value: "5", label: "Extremely strong", color: colors.dbt.intensity[7] },
    ],
  },
  weekly: {
    title: "Key: Weekly Check-In",
    entries: [
      { value: "0", label: "Not at all", color: colors.dbt.intensity[0] },
      { value: "1", label: "A bit", color: colors.dbt.intensity[1] },
      { value: "2", label: "Somewhat", color: colors.dbt.intensity[2] },
      { value: "3", label: "Rather strong", color: colors.dbt.intensity[4] },
      { value: "4", label: "Very strong", color: colors.dbt.intensity[6] },
      { value: "5", label: "Extremely strong", color: colors.dbt.intensity[7] },
    ],
  },
}

export type DBTTableKeyProps = {
  className?: string
  keyType?: DBTKeyType
  defaultExpanded?: boolean
  collapsedLabel?: string
}

const COLLAPSED_LABELS: Record<DBTKeyType, string> = {
  meals: "Show Meals key",
  skills: "Show Skills key",
  emotions: "Show Emotions key",
  urges: "Show Urges & Behaviors key",
  weekly: "Show key",
}

/**
 * DBTTableKey renders an expandable legend for the diary card tables.
 * It supports three presets today (meals, used skills, emotions/urges), and can easily be extended.
 */
const DBTTableKey: FC<DBTTableKeyProps> = ({
  className,
  keyType = "meals",
  defaultExpanded = false,
  collapsedLabel,
}) => {
  const classes = useStyles()
  const [isOpen, setIsOpen] = useState(defaultExpanded)
  const collection = useMemo(() => KEY_COLLECTIONS[keyType], [keyType])

  const toggleLabel = isOpen
    ? "Hide key"
    : collapsedLabel ?? COLLAPSED_LABELS[keyType]

  return (
    <div className={clsx(classes.container, className)}>
      {!isOpen && (
        <button
          className={classes.toggleButton}
          onClick={() => setIsOpen(true)}
          type="button"
        >
          <span>{toggleLabel}</span>
          <span className={clsx(classes.icon, classes.plus)} aria-hidden>
            +
          </span>
        </button>
      )}

      {isOpen && (
        <div className={classes.card}>
          <div className={classes.title}>{collection.title}</div>
          <div className={classes.legendBody}>
            {collection.entries.map((entry) => (
              <div
                key={`${entry.value}-${entry.label}`}
                className={classes.legendRow}
              >
                <span
                  className={classes.dot}
                  style={{ backgroundColor: entry.color }}
                />
                <span className={classes.legendText}>
                  {entry.value} - {entry.label}
                </span>
              </div>
            ))}
          </div>
          <button
            className={classes.toggleButton}
            onClick={() => setIsOpen(false)}
            type="button"
          >
            <span>Hide key</span>
            <span className={clsx(classes.icon, classes.close)} aria-hidden>
              ×
            </span>
          </button>
        </div>
      )}
    </div>
  )
}

const useStyles = makeStyles({
  container: {
    display: "inline-flex",
    flexDirection: "column",
    alignItems: "center",
  },
  card: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 8,
    backgroundColor: "rgba(0, 0, 0, 0.03)",
    borderRadius: 16,
    padding: "8px 12px 24px",
    border: "1px solid rgba(0, 0, 0, 0.03)",
  },
  title: {
    fontFamily: "Lato, sans-serif",
    fontWeight: 700,
    fontSize: 18,
    lineHeight: "20px",
    color: "#474747",
    textAlign: "center",
  },
  legendBody: {
    backgroundColor: "rgba(255,255,255,0.85)",
    borderRadius: 8,
    padding: "12px 8px 12px 16px",
    display: "flex",
    flexDirection: "column",
    gap: 8,
    minWidth: 180,
  },
  legendRow: {
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  dot: {
    width: 12,
    height: 12,
    borderRadius: "50%",
    flexShrink: 0,
  },
  legendText: {
    fontFamily: "Lato, sans-serif",
    fontSize: 14,
    lineHeight: "16px",
    color: "#737373",
  },
  toggleButton: {
    display: "inline-flex",
    gap: 4,
    alignItems: "center",
    justifyContent: "center",
    background: "transparent",
    border: "none",
    color: colors.primary.shadow,
    fontFamily: "Lato, sans-serif",
    fontWeight: 600,
    fontSize: 16,
    lineHeight: "20px",
    cursor: "pointer",
    padding: 0,
  },
  icon: {
    fontSize: 16,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: 16,
    height: 16,
    color: colors.primary.shadow,
  },
  plus: {
    transform: "translateY(-1px)",
  },
  close: {
    transform: "translateY(-1px)",
  },
})

export default DBTTableKey
